import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const ThemeMode = {
  light: 'light',
  dark: 'dark',
  auto: 'auto'
} as const

export type ThemeMode = (typeof ThemeMode)[keyof typeof ThemeMode]
type EffectiveTheme = Omit<ThemeMode, 'auto'>

const availableThemes: ThemeMode[] = ['light', 'dark', 'auto']


export const useThemeStore = defineStore('theme', () => {
  const currentTheme = ref<ThemeMode>('auto')
  const effectiveTheme = ref<EffectiveTheme>('dark')

  const isDarkMode = computed(() => effectiveTheme.value === 'dark')

  const initializeTheme = () => {
    // Dark mode only — light mode disabled
    currentTheme.value = 'dark'
    effectiveTheme.value = 'dark'
    applyTheme()
  }

  const initEffectiveThemeWithLocalStorage = () => {
    // Kept for compatibility but unused — dark mode is always forced
    currentTheme.value = 'dark'
    effectiveTheme.value = 'dark'
  }

  const addDarkModeMediaQueryListener = () => {
    const prefersDarkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    prefersDarkMediaQuery.addEventListener('change', handleSystemThemeChange)
  }

  const getSystemPrefersDark = () => {
    const prefersDarkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    return prefersDarkMediaQuery.matches
  }

  const handleSystemThemeChange = (event: MediaQueryListEvent) => {
    const localStorageTheme = localStorage.getItem('theme-preference') as ThemeMode

    if (!!localStorageTheme && localStorageTheme !== 'auto') return

    const prefersDark = event.matches

    effectiveTheme.value = prefersDark ? 'dark' : 'light'

    applyTheme()
  }

  const applyTheme = () => {
    let shouldBeDark = effectiveTheme.value === 'dark'

    if (shouldBeDark) {
      document.body.classList.add('dark')
      document.documentElement.setAttribute('data-theme', 'dark')
      document.documentElement.setAttribute('data-t', 'dark')
    } else {
      document.body.classList.remove('dark')
      document.documentElement.setAttribute('data-theme', 'light')
      document.documentElement.setAttribute('data-t', 'light')
    }
  }

  const setTheme = (_theme: ThemeMode) => {
    // Dark mode only — always force dark regardless of argument
    currentTheme.value = 'dark'
    effectiveTheme.value = 'dark'
    applyTheme()
  }

  const toggleTheme = () => {
    const currentThemeIndex = availableThemes.findIndex(theme => theme === currentTheme.value)
    const nextThemeIndex = (currentThemeIndex + 1) >= availableThemes.length ? 0 : currentThemeIndex + 1
    const nextTheme = availableThemes[nextThemeIndex]

    setTheme(nextTheme)
  }

  const getCurrentTheme = (): ThemeMode => {
    return currentTheme.value
  }

  return {
    currentTheme,
    isDarkMode,
    initializeTheme,
    setTheme,
    toggleTheme,
    getCurrentTheme,
  }
}) 